import {
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import { faTemperature2 } from "@fortawesome/free-solid-svg-icons/faTemperature2";
import { faDroplet } from "@fortawesome/free-solid-svg-icons";
import { faCalendar } from "@fortawesome/free-solid-svg-icons/faCalendar";
import { gql } from "graphql-tag";
import { responseField } from "../../../../store/base.graphql";
import { StoreItemActions } from "../../../../store/entities/store-item/store-item.actions";
import { StoreItemEntity } from "../../../../store/entities/store-item/store-item.model";
import { Store } from "@ngrx/store";
import { AppState } from "../../../../store";
import { selectStoreItems } from "../../../../store/entities/store-item/store-item.selectors";
import { Warehouse } from "../../../../store/entities/warehouse-management/warehouse/warehouse.model";
import { Observable, of, Subscription } from "rxjs";
import { GET_ALL_MSD_WAREHOUSE } from "../../../../store/entities/warehouse-management/warehouse/warehouse.graphql";
import {
  BaseResponse,
} from "../../../../common/interfaces/global-interfaces";
import { DataService } from "../../../../services/data.service";
import { GraphqlService } from "../../../../services/graphql.service";
import { NotificationService } from "../../../../services/notification.service";
import { Device } from "../../../../store/entities/warehouse-management/device/device.model";
import { GET_ALL_SENSORS } from "../../../../store/entities/warehouse-management/device/device.graphql";

export const GET_WAREHOUSE_COLDROOM_READINGS_REPORT = gql`
query GetWarehouseColdroomReadingsReport($filtering: ReportsFilteringInputObject) {
  getWarehouseColdroomReadingsReport(filtering: $filtering) {
    response {
      ${responseField}
    }
    data {
      reportData
    }
  }
}
`;

@Component({
  selector: "app-coldroom-report",
  templateUrl: "./coldroom-report.component.html",
  styleUrl: "./coldroom-report.component.scss",
})
export class ColdroomReportComponent implements OnInit {
  @Input() isAllWarehouse: boolean = true;

  private store: Store<AppState> = inject(Store<AppState>);
  private ds: DataService = inject(DataService);
  private apollo: GraphqlService = inject(GraphqlService);
  private notificationService: NotificationService =
    inject(NotificationService);

  timeframeTabs: { name: string }[] = [
    { name: "Today" },
    { name: "Yesterday" },
  ];
  @ViewChild("reportContent", { static: false }) reportContent!: ElementRef;
  activeTimeframeTab: string;

  frequencyTabs: { name: string }[] = [
    { name: "Hourly" },
    { name: "Daily" },
    { name: "Weekly" },
    { name: "Monthly" },
  ];
  activeFrequencyTab: string = "Hourly";

  endDate: string;
  startDate: string;
  warehouseSensors$: Observable<Device[]>;

  warehouses$: Observable<Warehouse[]>;
  filtering: {
    warehouse: string;
    sensor: string;
    dateTo: string;
    dateFrom: string;
    timeRange: string;
  } = {
    warehouse: "",
    sensor: null,
    dateFrom: this.ds.formatDate(new Date()) + "T00:00:00",
    dateTo: this.ds.formatDate(new Date()) + "T23:59:59",
    timeRange: "Hourly",
  };

  reportData: string;
  private warehouseSub: Subscription;

  ngOnInit() {
    this.activeTimeframeTab = "Today";

    this.store.dispatch(
      StoreItemActions.getStoreItems({
        entity: StoreItemEntity.warehouse,
        query: GET_ALL_MSD_WAREHOUSE,
        filtering: {},
      }),
    );

    this.warehouses$ = this.store.select(
      selectStoreItems(StoreItemEntity.warehouse),
    );

    this.generateReport();
  }

  ngOnDestroy() {
    if (this.warehouseSub) {
      this.warehouseSub.unsubscribe();
    }
  }

  onFilterChange(
    evt: string,
    filterType: "timeframe" | "timeRange" | "warehouse" | "dateFrom" | "dateTo",
  ) {
    switch (filterType) {
      case "timeRange":
        this.filtering.timeRange =
          evt.charAt(0).toUpperCase() + evt.slice(1).toLowerCase();
        break;

      case "timeframe":
        this.startDate = null;
        this.endDate = null;
        switch (evt) {
          case "Today":
            this.filtering.dateFrom =
              this.ds.formatDate(new Date()) + "T00:00:00";
            this.filtering.dateTo =
              this.ds.formatDate(new Date()) + "T23:59:59";
            break;
          case "Yesterday":
            let yesterdayDate = new Date();
            yesterdayDate.setDate(yesterdayDate.getDate() - 1);
            this.filtering.dateFrom =
              this.ds.formatDate(yesterdayDate) + "T00:00:00";
            this.filtering.dateTo =
              this.ds.formatDate(yesterdayDate) + "T23:59:59";
            break;
        }
        break;
      case "dateFrom":
        this.filtering.dateFrom =
          this.ds.formatDate(this.startDate) + "T00:00:00";
        break;
      case "dateTo":
        this.filtering.dateTo = this.ds.formatDate(this.endDate) + "T23:59:59";
        break;

      case "warehouse":
        if (this.filtering.warehouse) {
          this.store.dispatch(
            StoreItemActions.getStoreItems({
              entity: StoreItemEntity.warehouseSensor,
              query: GET_ALL_SENSORS,
              filtering: {
                warehouse: this.filtering.warehouse,
                pageNumber: 1,
                itemsPerPage: 200,
              },
            }),
          );

          this.warehouseSensors$ = this.store.select(
            selectStoreItems(StoreItemEntity.warehouseSensor),
          );
        }

        this.filtering.sensor = null;
        break;
    }
  }

  async generateReport() {
    try {
      const response: any = await this.apollo.query({
        query: GET_WAREHOUSE_COLDROOM_READINGS_REPORT,
        variables: {
          filtering: this.filtering,
        },
      });

      const result: BaseResponse<{ reportData: string }> = Object.values(
        Object.values(response)[0],
      )[0];

      if (result?.response?.status) {
        this.reportData = result?.data?.reportData;
      } else {
        throw Error(result.response.message);
      }
    } catch (e) {
      this.notificationService.errorMessage(e.message);
    }
  }

  protected readonly faTemperature2 = faTemperature2;
  protected readonly faDroplet = faDroplet;
  protected readonly faCalendar = faCalendar;
  protected readonly Math = Math;
  protected readonly Array = Array;
  protected readonly of = of;
}
